import { useEffect, useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import { useMount } from 'react-use'

import ProductTextNodes from '@data/i18n/product.json'

import useCanAddToCart from '@lib/cart/use-can-add-to-cart'
import useCart from '@context/useCartContext'
import { CURRENCY_MAP, WEIGHT_UNIT_MAP } from '@lib/constants'
import type { Breadcrumbs, Image as ImageType } from '@lib/types'
import type {
	ProductCard,
	Product as ProductType,
	ProductVariant,
	SellingPlan
} from '@lib/types/product'
import { parseMarkdown } from '@lib/utils/markdown'
import { removeEmptyHeadings } from '@lib/utils/helpers'

import StarRatings from '@components/StarRatings'
import PageBreadcrumbs from '@components/global/Breadcrumbs'
import WholePageModal from '@components/global/WholePageModal'
import ProductsCarousel from '@components/products/ProductsCarousel'

import MobileLogo from '@components/global/MobileLogo'
import AddToCart from './AddToCart'
import Images from './Images'
import Options from './Options'
// import OutOfStockNotification from './OutOfStockNotification'
import Price from './Price'
import Subscription from './Subscription'

type Props = {
	product: ProductType
	breadcrumbs?: Breadcrumbs | null
	recommendations?: ProductCard[]
	isFavorite?: boolean
	showFavorite?: boolean
	backButton?: { path: string; title: string }
	onAddToCart?: () => void
}

export default function ProductView({
	product,
	breadcrumbs,
	recommendations,
	isFavorite = false,
	showFavorite = true,
	backButton,
	onAddToCart
}: Props) {
	const [loading, setLoading] = useState<boolean>(false)
	const [selectedOptions, setSelectedOptions] = useState<ProductVariant['selectedOptions']>(
		product?.variants[0]?.selectedOptions
	)
	const router = useRouter()
	const { openCart } = useCart()
	const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(product?.variants[0])
	const [selectedSellingPlanId, setSelectedSellingPlanId] = useState<SellingPlan['id'] | null>(null)
	const [modalOpen, setModalOpen] = useState(false)

	const [ingredientsListExpanded, setIngredientsListExpanded] = useState(false)
	const [descriptionExpanded, setDescriptionExpanded] = useState(false)

	const [currentImage, setCurrentImage] = useState({
		url: product?.images[0]?.url,
		width: product?.images[0]?.width,
		height: product?.images[0]?.height,
		altText: product?.images[0]?.altText
	})

	const canAddToCart = useCanAddToCart({
		product,
		variant: selectedVariant
	})

	const closeModal = () => {
		setModalOpen(false)
	}

	const goToPath = (path: string) => router.push(path)

	const setCurrentImageHandler = (image: ImageType) => {
		if (image?.url)
			setCurrentImage({
				url: image?.url,
				width: image?.width,
				height: image?.height,
				altText: image?.altText
			})
	}

	// Set the product variant later
	useEffect(() => {
		setSelectedVariant(product?.variants[0])
		setCurrentImageHandler(product?.images[0])
	}, [product])

	/**
	 * Load Loox script dynamically
	 */
	useMount(() => {
		if (!product?.tags?.includes('no-review')) {
			const looxScript = document.createElement('script')
			looxScript.id = 'looxScript'
			looxScript.async = true
			looxScript.src = `https://loox.io/widget/loox.js?shop=${process.env.NEXT_PUBLIC_STORE_DOMAIN}`

			// insert the script right after the widget element
			const looxWidget = document.getElementById('looxReviews') as HTMLDivElement
			looxWidget.after(looxScript)
		}
	})

	return (
		<>
			{/* Breadcrumb nav */}
			{breadcrumbs && (
				<div className="container mt-12">
					<PageBreadcrumbs breadcrumbs={breadcrumbs} />
				</div>
			)}

			{/* Back Button */}
			{!breadcrumbs && backButton && (
				<div className="container mt-6">
					<Link
						legacyBehavior
						href={{
							pathname: backButton.path,
							query: router.query.collection_name
								? { collection_name: router.query.collection_name }
								: {}
						}}
					>
						<a
							// type="button"
							className="button button-text item-center"
							// onClick={() => goToPath(backButton.path)}
						>
							<svg
								className=""
								fill="#041E42"
								height="16"
								viewBox="0 -960 960 960"
								width="16"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="m384-109.85-370.15-370.15 370.15-370.15 53.77 53.77-316.39 316.38 316.39 316.38z" />
							</svg>
							{backButton.title}
						</a>
					</Link>
				</div>
			)}

			{/* Product */}
			<section className="relative pt-0 mt-4 lg:mt-16 section">
				<div className="container flex flex-col items-start gap-x-16 gap-y-10 md:flex-row">
					{/* Product image(s) */}
					<div className="relative w-full md:w-1/2 md:sticky md:top-24">
						{product?.vendor?.toLowerCase() === 'birchbox' && (
							<MobileLogo className="absolute top-2 right-2 z-10 w-8 fill-bbx-style opacity-30" />
						)}
						{/* <pre>{JSON.stringify(product, null, 2)}</pre> */}
						{product?.images?.length > 0 ? (
							<Images
								id={product.id}
								productImages={product?.images}
								setModalOpen={setModalOpen}
								currentImage={currentImage}
								setCurrentImageHandler={setCurrentImageHandler}
								isFavorite={isFavorite}
								showFavorite={showFavorite}
							/>
						) : (
							<div
								className="flex items-center justify-center bg-gray-100 h-96"
								title="This product has no image."
							>
								<i className="text-gray-300 fa-thin fa-image-slash text-7xl" />
							</div>
						)}
					</div>

					{/* Product metadata */}
					<div className="w-full md:w-1/2">
						{/* Brand */}
						{/* TODO: route this to proper page, otherwise remove link */}
						{product?.vendor && (
							<span className="inline-block mb-3 text-sm font-medium">{product?.vendor}</span>
						)}

						{/* Product Name */}
						<h1 className="mb-4 product title">{product.name}</h1>

						{/* Reviews */}
						{product?.numReviews && !product?.tags?.includes('no-review') && (
							<div className="flex items-center py-4 mb-10 font-medium border-y gap-x-3">
								<StarRatings avgRating={product?.avgRating} />
								<span className="-mb-1 text-xs">
									{product?.numReviews.toLocaleString()}{' '}
									{product?.numReviews === 1 ? 'review' : 'reviews'}
								</span>
							</div>
						)}

						{/* Price */}
						<div>
							{selectedVariant?.compareAtPrice &&
								Number(selectedVariant.compareAtPrice.amount) >
									Number(selectedVariant.price.amount) && (
									<span className="line-through text-gray-600 text-sm">
										Was <Price price={selectedVariant.compareAtPrice} />
									</span>
								)}

							<div className="mb-8 text-3xl flex items-center">
								{selectedVariant?.price && selectedVariant.price?.amount !== '0' && (
									<span
										className={`${selectedVariant?.compareAtPrice && Number(selectedVariant.compareAtPrice.amount) > Number(selectedVariant.price.amount) && 'text-bbx-red'}`}
									>
										<Price price={selectedVariant.price} />
									</span>
								)}

								{selectedVariant?.compareAtPrice &&
									Number(selectedVariant.compareAtPrice.amount) >
										Number(selectedVariant.price.amount) && (
										<span className="bg-bbx-red text-white px-2 py-1 text-sm rounded-sm ml-2">
											SAVE{' '}
											{Math.round(
												((Number(selectedVariant.compareAtPrice.amount) -
													Number(selectedVariant.price.amount)) /
													Number(selectedVariant.compareAtPrice.amount)) *
													100
											)}
											%
										</span>
									)}
							</div>
						</div>

						{/* Description */}
						<div className="mb-6">
							<div
								className={`description-wrapper ${descriptionExpanded ? '' : 'line-clamp-4'}`}
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: removeEmptyHeadings(product.descriptionHtml || '')
								}}
							/>
							<button
								className="mt-1 button button-text font-normal pr-0 pl-0 min-w-0 font-bold"
								type="button"
								onClick={() => setDescriptionExpanded(!descriptionExpanded)}
							>
								{descriptionExpanded
									? ProductTextNodes.product.collapse
									: ProductTextNodes.product.readMore}
							</button>
						</div>

						{/* Options */}
						<Options
							product={product}
							selectedOptions={selectedOptions}
							setSelectedOptions={setSelectedOptions}
							setSelectedVariant={setSelectedVariant}
							setCurrentImageHandler={setCurrentImageHandler}
						/>

						{/* Subscription Box */}
						{/* Show the subscription modal if the product has selling plans */}
						{product.sellingPlanGroups.length > 0 && (
							<Subscription
								selectedSellingPlanId={selectedSellingPlanId}
								setSelectedSellingPlanId={setSelectedSellingPlanId}
								sellingPlanGroups={product.sellingPlanGroups}
							/>
						)}

						{!product.notForSale && (
							<>
								<div>
									{/* Add to cart */}
									{/* Fixed on mobile */}
									<div className="max-md:fixed max-md:z-20 max-md:inset-x-0 max-md:bottom-0 max-md:grid max-md:p-2 max-md:bg-white max-md:border-t">
										{/* Price available only on mobile sticky add to cart button */}
										<AddToCart
											product={product}
											variant={selectedVariant}
											loading={loading}
											setLoading={setLoading}
											sellingPlanId={selectedSellingPlanId}
											priceText={`${CURRENCY_MAP.get(selectedVariant?.price.currencyCode)}${selectedVariant?.price.amount}`}
											breadcrumbs={breadcrumbs}
											onAddToCart={() => {
												onAddToCart?.()
												openCart()
											}}
										/>
									</div>

									{/* Hide this button if the product requires a selling plan */}
									{/* i.e. the product is a box */}

									{/*
										HIDDEN FOR NOW
										High likelihood that it will be a fast-follow item (July-August), and not part of V2 launch requirements. -- Jackie Melle
									*/}
									{/* {!product.requiresSellingPlan && (
										<button className="button button-secondary" type="button">
											Ship with Box
										</button>
									)} */}
								</div>

								{/* Email reminder if out of stock */}
								{/* {canAddToCart === false && <OutOfStockNotification product={product} />} */}
							</>
						)}

						{/* Additional Information */}
						{product.promotionalDescription && (
							// TODO: @TJ check/review this
							<div
								className="text-sm text-gray-500"
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{ __html: parseMarkdown(product.promotionalDescription) }}
							/>
						)}

						{/* Product size/weight */}
						{selectedVariant?.weight > 0 && (
							<p className="mt-1">
								{/* FIXME: @Drayke or @TJ style/position */}
								{/* Not sure we even need to be showing this? — TJ */}
								<span className="font-medium">Weight: </span>
								<span>
									{selectedVariant.weight}
									{selectedVariant.weightUnit && WEIGHT_UNIT_MAP[selectedVariant.weightUnit]}
								</span>
							</p>
						)}
					</div>
				</div>
			</section>

			{/* More info */}
			{(product?.highlights || product?.howToUse) && (
				<section className="section bg-bbx-pearl-lightest">
					<div className="container">
						{/* Highlights */}
						{product?.highlights && (
							<div className="pb-16 mb-16 border-b">
								<h2 className="mb-8 text-3xl title">{ProductTextNodes.product.highlights}</h2>
								<ul className="flex flex-wrap items-center gap-x-16 gap-y-8">
									{product.highlights.map((highlight) => (
										<li key={`${highlight.name}-${highlight.icon}`} className="flex items-center">
											{/* Icon */}
											<div className="flex items-center justify-center mr-3 text-2xl rounded-full w-14 h-14 bg-bbx-pearl text-bbx-spark">
												<i className={`fa-regular fa-${highlight.icon}`} />
											</div>
											<span className="font-medium">{highlight.name}</span>
										</li>
									))}
								</ul>
							</div>
						)}

						{/* How to/Ingredients */}
						<div className="grid grid-cols-1 gap-16 md:grid-cols-2">
							{/* How to use */}
							{product?.howToUse && (
								<div>
									<h2 className="mb-4 text-3xl title">{ProductTextNodes.product.howto}</h2>

									<div
										className="max-w-[480px]"
										// eslint-disable-next-line react/no-danger
										dangerouslySetInnerHTML={{ __html: parseMarkdown(product.howToUse) }}
									/>
								</div>
							)}{' '}
							{/* Ingredients */}
							{product.ingredients && product.ingredients.length > 0 && (
								<div className="p-8 bg-white">
									<h2 className="mb-4 text-xl font-medium">
										{ProductTextNodes.product.ingredients}
									</h2>

									<ul
										className={`break-all ${ingredientsListExpanded ? '' : 'h-40 overflow-hidden'}`}
									>
										{product.ingredients.map((ingredient, i) => (
											// indexes won't change dynamically
											// eslint-disable-next-line react/no-array-index-key
											<li key={ingredient + i}>{ingredient}</li>
										))}
									</ul>
									<button
										className={`mt-1 button button-link ${
											// Don't show the button if the list is too short for it to be necessary
											product.ingredients.length <= 8 ? 'hidden' : ''
										}`}
										type="button"
										onClick={() => setIngredientsListExpanded(!ingredientsListExpanded)}
									>
										{ingredientsListExpanded
											? ProductTextNodes.product.collapse
											: ProductTextNodes.product.viewall}
									</button>
									{/* TODO: Do this eventually with proper JSON as per design */}
									{/* <dl>
								{ingredients.map((ingredient, index) => (
									// eslint-disable-next-line react/no-array-index-key
									<Fragment key={index}>
										<dt className="mt-4 font-medium">{ingredient.name}</dt>
										<dd>{ingredient.description}</dd>
									</Fragment>
								))}
							</dl> */}
								</div>
							)}
						</div>
					</div>
				</section>
			)}

			{/* Recommendations */}
			{recommendations && (
				<ProductsCarousel
					title={ProductTextNodes.product.recommendations.title}
					products={recommendations}
				/>
			)}

			{/* Reviews */}
			{!product?.tags?.includes('no-review') && (
				<section className="section">
					<div className="container">
						<h2 className="mb-4 text-3xl title">{ProductTextNodes.product.reviews}</h2>

						<div
							id="looxReviews"
							data-product-id={
								// use only product id numbers
								product.id.replace(/[^0-9]+/, '')
							}
						/>
					</div>
				</section>
			)}

			{/* Modal */}
			{modalOpen && (
				<WholePageModal
					currentImage={currentImage}
					productImages={product?.images}
					setCurrentImageHandler={setCurrentImageHandler}
					closeModal={closeModal}
				/>
			)}
		</>
	)
}
